import { useState, useEffect } from 'react';

// Currency conversion rates (approximate, from USD)
const CONVERSION_RATES: Record<string, number> = {
  'USD': 1,
  'EUR': 0.92,
  'GBP': 0.79,
  'CAD': 1.36,
  'AUD': 1.52,
  'NZD': 1.64,
  'JPY': 149.50,
  'INR': 83.12,
  'BRL': 4.97,
  'MXN': 17.05,
  'ZAR': 18.65,
  'SGD': 1.34,
  'HKD': 7.83,
};

// Map regions to currencies
const REGION_CURRENCY_MAP: Record<string, string> = {
  'US': 'USD',
  'CA': 'CAD',
  'GB': 'GBP',
  'UK': 'GBP',
  'AU': 'AUD',
  'NZ': 'NZD',
  'JP': 'JPY',
  'IN': 'INR',
  'BR': 'BRL',
  'MX': 'MXN',
  'ZA': 'ZAR',
  'SG': 'SGD',
  'HK': 'HKD',
};

// EU countries
const EU_COUNTRIES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
];

export const useCurrency = () => {
  const [currency, setCurrency] = useState<string>('USD');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectCurrency();
  }, []);

  const detectCurrency = () => {
    try {
      // Try to get currency from browser locale
      const locale = navigator.language || 'en-US';
      
      // Extract region code (e.g., 'US' from 'en-US')
      const regionCode = locale.split('-')[1]?.toUpperCase();
      
      // Check if it's an EU country
      if (regionCode && EU_COUNTRIES.includes(regionCode)) {
        setCurrency('EUR');
      } else if (regionCode && REGION_CURRENCY_MAP[regionCode]) {
        setCurrency(REGION_CURRENCY_MAP[regionCode]);
      } else {
        // Try to detect from Intl API
        try {
          const detectedCurrency = new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: 'USD'
          }).resolvedOptions().currency;
          
          if (detectedCurrency && CONVERSION_RATES[detectedCurrency]) {
            setCurrency(detectedCurrency);
          }
        } catch {
          // Fallback to USD
          setCurrency('USD');
        }
      }
    } catch (error) {
      console.error('Error detecting currency:', error);
      setCurrency('USD');
    } finally {
      setLoading(false);
    }
  };

  const convertPrice = (usdPrice: number): number => {
    const rate = CONVERSION_RATES[currency] || 1;
    return usdPrice * rate;
  };

  const formatPrice = (usdPrice: number): string => {
    const convertedPrice = convertPrice(usdPrice);
    
    try {
      return new Intl.NumberFormat(navigator.language, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(convertedPrice);
    } catch {
      // Fallback formatting
      return `${getCurrencySymbol(currency)}${convertedPrice.toFixed(2)}`;
    }
  };

  const getCurrencySymbol = (currencyCode: string): string => {
    const symbols: Record<string, string> = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'CAD': 'CA$',
      'AUD': 'A$',
      'NZD': 'NZ$',
      'JPY': '¥',
      'INR': '₹',
      'BRL': 'R$',
      'MXN': 'MX$',
      'ZAR': 'R',
      'SGD': 'S$',
      'HKD': 'HK$',
    };
    return symbols[currencyCode] || currencyCode;
  };

  return {
    currency,
    loading,
    convertPrice,
    formatPrice,
    getCurrencySymbol,
  };
};
